import { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Grid, Alert,
  Card, CardContent, Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  AccountBalance as AccountingIcon,
} from '@mui/icons-material';
import { API_CONFIG, API_ENDPOINTS } from '../config/api';
import { api } from '../services/apiClient';

interface Summary {
  totals: {
    invoice_count: number; total_subtotal: number; total_tax: number;
    total_gross: number; total_paid: number; total_outstanding: number;
  };
  by_status: { status: string; count: number; total: number }[];
  by_month: { month: string; count: number; total: number }[];
  period: { date_from: string | null; date_to: string | null };
}

const fmt = (n: number | null | undefined) =>
  n != null ? `RM ${Number(n).toFixed(2)}` : 'RM 0.00';

export default function AccountingExportPage() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [format, setFormat] = useState<'csv' | 'xero' | 'quickbooks'>('csv');
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleDownload() {
    setDownloading(true); setError('');
    try {
      const params = new URLSearchParams({ format });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const url = `${API_CONFIG.FULL_URL}${API_ENDPOINTS.ACCOUNTING.EXPORT}?${params}`;
      const accessToken = localStorage.getItem('access_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const cd = res.headers.get('content-disposition') ?? '';
      const name = cd.match(/filename="(.+)"/)?.[1] ?? `export-${format}.csv`;
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  async function loadSummary() {
    setSummaryLoading(true); setError('');
    try {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const qs = new URLSearchParams(params).toString();
      const res = await api.get<Summary>(
        `${API_ENDPOINTS.ACCOUNTING.SUMMARY}${qs ? '?' + qs : ''}`,
      );
      setSummary(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AccountingIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Accounting Export</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>Export Invoices</Typography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Date From" type="date"
              value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Date To" type="date"
              value={dateTo} onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Format</InputLabel>
              <Select value={format} label="Format"
                onChange={e => setFormat(e.target.value as 'csv' | 'xero' | 'quickbooks')}>
                <MenuItem value="csv">Generic CSV</MenuItem>
                <MenuItem value="xero">Xero Import</MenuItem>
                <MenuItem value="quickbooks">QuickBooks Import</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownload} disabled={downloading} fullWidth>
              {downloading ? 'Downloading…' : 'Download'}
            </Button>
            <Button variant="outlined" onClick={loadSummary} disabled={summaryLoading}>
              {summaryLoading ? <CircularProgress size={20} /> : 'Summary'}
            </Button>
          </Grid>
        </Grid>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      {summary && (
        <Box>
          <Typography variant="h6" mb={2}>Revenue Summary</Typography>
          <Grid container spacing={2} mb={3}>
            {[
              { label: 'Total Invoices', value: String(summary.totals?.invoice_count ?? 0) },
              { label: 'Gross Revenue', value: fmt(summary.totals?.total_gross) },
              { label: 'Total Paid', value: fmt(summary.totals?.total_paid) },
              { label: 'Outstanding', value: fmt(summary.totals?.total_outstanding) },
            ].map(stat => (
              <Grid item xs={6} sm={3} key={stat.label}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h6" color="primary">{stat.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {summary.by_status.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>By Status</Typography>
              <Divider sx={{ mb: 1 }} />
              {summary.by_status.map(row => (
                <Box key={row.status} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{row.status}</Typography>
                  <Typography variant="body2">{row.count} invoices — {fmt(row.total)}</Typography>
                </Box>
              ))}
            </Paper>
          )}

          {summary.by_month.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>Monthly Revenue</Typography>
              <Divider sx={{ mb: 1 }} />
              {summary.by_month.map(row => (
                <Box key={row.month} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{row.month}</Typography>
                  <Typography variant="body2">{row.count} invoices — {fmt(row.total)}</Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}
